"use client";

import { useEffect } from "react";

import {
  isConsentGranted,
  waitForConsentApi
} from "@/src/lib/datagrail/consent";
import { toConsentModeSignals } from "./consentMode";

/**
 * Bridges DataGrail's consent state to GTM Consent Mode v2.
 *
 * The `beforeInteractive` defaults deny ad storage; this pushes the `update`
 * that releases it once DataGrail can answer. If DataGrail never loads —
 * blocked, usually — `waitForConsentApi` resolves false and no update is sent,
 * so the denied defaults stand. That is the deliberate opposite of
 * `isAdPersonalizationRestricted`, which fails open for GAM: an unreadable
 * DataGrail must not silently grant ad consent to Google's tags.
 *
 * Mid-session preference changes are not observed. DataGrail exposes
 * `initial_preference_callback` (see `datagrail/optOut.ts`) but no documented
 * change event, and its banner reloads the page on save.
 */
export function useDataGrailConsentMode() {
  useEffect(() => {
    const controller = new AbortController();

    void waitForConsentApi({ signal: controller.signal }).then(isReady => {
      if (!isReady || controller.signal.aborted) return;
      window.gtag?.(
        "consent",
        "update",
        toConsentModeSignals(isConsentGranted)
      );
    });

    return () => controller.abort();
  }, []);
}
